import { useState, useEffect } from 'react'
import { createRoute, useNavigate } from '@tanstack/react-router'
import { FileText, RefreshCw, Loader2, FileCheck, FileClock, Eye, Plus } from 'lucide-react'
import { lawyerRoute } from './index'
import { lawyerCasesAPI } from '@/utils/api'
import { CasesTable } from '@/components/lawyer/CasesTable'
import { CaseSearch } from '@/components/lawyer'
import { CreateCaseModal } from '@/components/lawyer/CreateCaseModal'

interface Case {
  id: number
  token: string
  client_name: string
  state: 'draft' | 'submitted'
  state_display: string
  incident_type: string | null
  incident_date: string | null
  airline_code: string | null
  document_count: number
  created_at: string
  updated_at: string
  is_draft: boolean
}

function CasesPage() {
  const [cases, setCases] = useState<Case[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const fetchCases = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }
    setError(null)

    try {
      const response = await lawyerCasesAPI.getCases()
      setCases(response.cases || [])
    } catch (err) {
      console.error('Failed to fetch cases:', err)
      setError('Fälle konnten nicht geladen werden. Bitte erneut versuchen.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchCases()
  }, [])

  const draftCount = cases.filter((c) => c.is_draft).length
  const submittedCount = cases.filter((c) => !c.is_draft).length

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-slate-400" />
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Fälle</h1>
            <p className="text-slate-500">Entschädigungsfälle verwalten</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CreateCaseModal
            onCreated={() => fetchCases(true)}
            triggerButton={
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white transition-colors">
                <Plus className="h-4 w-4" />
                Neuer Fall
              </button>
            }
          />
          <button
            onClick={() => fetchCases(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-50"
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Aktualisieren
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <CaseSearch className="mb-6" />

      {/* Stats */}
      {!loading && !error && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <FileText className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{cases.length}</p>
                <p className="text-sm text-slate-500">Gesamtfälle</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-orange-200 shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <Eye className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{submittedCount}</p>
                <p className="text-sm text-slate-500">Prüfung erforderlich</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <FileCheck className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{submittedCount}</p>
                <p className="text-sm text-slate-500">Eingereicht</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <FileClock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{draftCount}</p>
                <p className="text-sm text-slate-500">Entwürfe</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-64 bg-white rounded-xl border border-slate-200 shadow-sm">
          <Loader2 className="h-8 w-8 text-slate-400 animate-spin mb-4" />
          <p className="text-slate-500">Fälle werden geladen...</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center h-64 bg-white rounded-xl border border-red-200 shadow-sm">
          <FileText className="h-16 w-16 text-red-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Fehler</h3>
          <p className="text-slate-500 text-center max-w-md mb-4">{error}</p>
          <button
            onClick={() => fetchCases()}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors"
          >
            Erneut versuchen
          </button>
        </div>
      ) : (
        <CasesTable cases={cases} />
      )}
    </div>
  )
}

export const lawyerCasesRoute = createRoute({
  getParentRoute: () => lawyerRoute,
  path: '/cases',
  component: CasesPage,
})
